import { useState } from 'react';
import { Card, Select, Button, Space, Alert, Typography, Tag, message } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { updateApp, type App } from '@/api/apps';

const { Text, Paragraph } = Typography;

/**
 * FlowEditor — apps.flow_config 的编辑界面 (flow-schema capability).
 *
 * 起新 App 不再硬编码流程: 选一组有序 step, 前端按它渲染/导航. 空=回退默认答题流.
 * 受支持 step 与后端 internal/flow.SupportedSteps 一致 (写入时后端会再校验).
 *
 * 两个预设一键填: 答题流 (现有 3 App) / 写真流 (套系换脸).
 */

const SUPPORTED_STEPS = [
  'quiz',
  'series_grid',
  'upload',
  'loading',
  'result',
  'result_photo',
  'paywall',
] as const;

const PRESET_QUIZ = ['quiz', 'upload', 'loading', 'result', 'paywall'];
const PRESET_PHOTO = ['series_grid', 'upload', 'loading', 'result_photo', 'paywall'];

export function FlowEditor({ app, onSaved }: { app: App; onSaved?: (a: App) => void }) {
  const [steps, setSteps] = useState<string[]>(app.flow_config?.steps ?? PRESET_QUIZ);
  const [saving, setSaving] = useState(false);

  const onSave = async () => {
    if (steps.length === 0) {
      message.error('流程至少需要一个 step');
      return;
    }
    setSaving(true);
    try {
      const updated = await updateApp(app.id, { flow_config: { steps } });
      message.success('流程已保存');
      onSaved?.(updated);
    } catch (e) {
      // 后端拒绝未支持的 step → 这里展示错误
      message.error(e instanceof Error ? e.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <Paragraph type="secondary">
        App 流程片段（有序）。前端按此顺序渲染/导航。空 = 回退默认答题流（现有 App 行为不变）。
      </Paragraph>

      <Space style={{ marginBottom: 16 }}>
        <Text>预设：</Text>
        <Button size="small" onClick={() => setSteps(PRESET_QUIZ)}>
          答题流
        </Button>
        <Button size="small" onClick={() => setSteps(PRESET_PHOTO)}>
          写真流（套系换脸）
        </Button>
      </Space>

      <Select
        mode="multiple"
        value={steps}
        onChange={(v) => setSteps(v)}
        style={{ width: '100%' }}
        placeholder="按顺序选择流程 step"
        options={SUPPORTED_STEPS.map((s) => ({ value: s, label: s }))}
      />

      <div style={{ margin: '16px 0' }}>
        <Text type="secondary">当前流程：</Text>{' '}
        {steps.length === 0 ? (
          <Text type="secondary">（空 → 默认答题流）</Text>
        ) : (
          steps.map((s, i) => (
            <Tag key={`${s}-${i}`} color="blue">
              {i + 1}. {s}
            </Tag>
          ))
        )}
      </div>

      {steps.includes('quiz') && steps.includes('series_grid') && (
        <Alert
          type="warning"
          showIcon
          message="quiz 与 series_grid 同时出现不常见——通常二选一作为生成入口。"
          style={{ marginBottom: 16 }}
        />
      )}

      <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={onSave}>
        保存流程
      </Button>
    </Card>
  );
}
